'use client';

import React, { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import { useTranslations } from 'next-intl';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { runScrollTriggerSetup } from 'utils/performanceLabScrollTrigger';
import {
  FixedBgContent,
  FixedBgImageWrap,
  FixedBgLayer,
  FixedBgSectionRoot,
  FixedBgViewportContent,
} from '../fixedBackgroundScroll';
import { ResponsiveParallaxBackgroundImages } from '../ResponsiveParallaxBackgroundImages';
import { IV_LOUNGE_BREAK_PARALLAX_BG } from './ivLoungePageAssets';

gsap.registerPlugin(ScrollTrigger);

const BG_FALLBACK = '#1d1d1d';
const SECTION_VH_MOBILE = 1;
const SECTION_VH_DESKTOP = 0.8;

/** Full-bleed lounge photo break — same parallax shell as PincodeCtaBannerSection. */
export const IvLoungePhotoBreak: React.FC = () => {
  const t = useTranslations('performance-lab');
  const sectionRef = useRef<HTMLElement>(null);
  const bgLayerRef = useRef<HTMLDivElement>(null);

  useIsomorphicLayoutEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    if (prefersReducedMotion) return;

    return runScrollTriggerSetup({
      debugLabel: 'IvLoungePhotoBreak parallax',
      isReady: () =>
        Boolean(sectionRef.current) && Boolean(bgLayerRef.current),
      setup: () => {
        const section = sectionRef.current;
        const bgLayer = bgLayerRef.current;
        if (!section || !bgLayer) return;

        const trigger = ScrollTrigger.create({
          trigger: section,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
          invalidateOnRefresh: true,
          onUpdate: (self) => {
            const eased = Math.min(self.progress / 0.5, 1);
            const scale = 1 + eased * 0.12;
            const translateY = (eased - 0.5) * -30;
            bgLayer.style.transform = `scale(${scale}) translateY(${translateY}px)`;
          },
        });

        return {
          cleanup: () => {
            trigger.kill();
            bgLayer.style.transform = '';
          },
        };
      },
    });
  }, []);

  return (
    <FixedBgSectionRoot
      ref={sectionRef}
      role="region"
      aria-label={t('ivLounge.photoBreak.aria')}
      $mobileVh={SECTION_VH_MOBILE}
      $desktopVh={SECTION_VH_DESKTOP}
      $fallbackBg={BG_FALLBACK}
    >
      <FixedBgLayer ref={bgLayerRef} aria-hidden>
        <FixedBgImageWrap $fallback={BG_FALLBACK}>
          <ResponsiveParallaxBackgroundImages src={IV_LOUNGE_BREAK_PARALLAX_BG} />
        </FixedBgImageWrap>
      </FixedBgLayer>
      <FixedBgContent>
        <FixedBgViewportContent aria-hidden />
      </FixedBgContent>
    </FixedBgSectionRoot>
  );
};
